import { Link as RouterLink } from 'react-router-dom';

import { Skeleton, Typography } from '@/ui';

import MomentsCard from './MomentsCard';
import NextSessionPrepCard from './NextSessionPrepCard';
import SessionReflectionRow from './SessionReflectionRow';

import type {
  HomeSessionAssetRow,
  NextSessionPrepHomeCard,
} from './sessionReflectionHomeUtils';
import type { FC } from 'react';

import {
  buildMemberMomentsAnalyticsProperties,
  MEMBER_MOMENTS_EVENTS,
  momentsAnalytics,
} from '@/services/momentsAnalytics';

interface SessionReflectionsModuleProps {
  allHighlightsHref?: string;
  hasReflectionError?: boolean;
  highlightsTotalCount?: number;
  isLoading?: boolean;
  prep?: NextSessionPrepHomeCard | null;
  rows: HomeSessionAssetRow[];
  showAllHref?: string;
}

const LoadingRows = () => (
  <div className="border-0 border-t border-solid border-cement-200 px-8 py-5">
    {[0, 1].map((row) => (
      <div key={row} className="flex items-center gap-4 py-3">
        <Skeleton className="h-5 w-[72px]" variant="rounded" />
        <Skeleton className="h-5 flex-1" variant="rounded" />
      </div>
    ))}
  </div>
);

// MEN-4919: Product chose one empty-state message for every journey phase.
const UNIVERSAL_SESSION_REFLECTIONS_EMPTY_MESSAGE =
  'As you meet with coaches, reflections from your sessions will appear here to help you carry what you learned forward.';

const EmptyState = () => (
  <div className="border-0 border-t border-solid border-cement-200 px-8 py-12 text-center md:px-40">
    <Typography className="text-cement-400" variant="body1">
      {UNIVERSAL_SESSION_REFLECTIONS_EMPTY_MESSAGE}
    </Typography>
  </div>
);

const allHighlightsLabel = (count: number) =>
  count > 0 ? `All Highlights (${count})` : 'All Highlights';

const footerLinkClass =
  'inline-flex rounded-xl border border-solid border-cement-300 px-4 py-2 text-sm font-bold text-cement-500 no-underline';

const formatSessionsShownLabel = (count: number) => {
  if (count === 0) {
    return null;
  }
  return count === 1
    ? 'Showing the last session'
    : `Showing the last ${count} sessions`;
};

const renderReflectionBody = (
  isLoading: boolean,
  rows: HomeSessionAssetRow[],
) => {
  if (isLoading) {
    return <LoadingRows />;
  }
  if (rows.length === 0) {
    return <EmptyState />;
  }
  return (
    <div>
      {rows.map((row) => (
        <SessionReflectionRow key={row.id} row={row} />
      ))}
    </div>
  );
};

const SessionReflectionsModule: FC<SessionReflectionsModuleProps> = ({
  allHighlightsHref = '/m/highlights',
  hasReflectionError = false,
  highlightsTotalCount = 0,
  isLoading = false,
  prep,
  rows,
  showAllHref = '/m/sessions/past',
}) => {
  const sessionsShownLabel = formatSessionsShownLabel(rows.length);
  const shouldShowReflectionFooter = rows.length > 0;

  return (
    <section className="mb-6 w-full max-w-[832px] px-4 md:px-0">
      <Typography className="mb-6 font-bold text-white" variant="h5">
        Moments
      </Typography>
      <div className="flex flex-col gap-6">
        {prep ? <NextSessionPrepCard prep={prep} /> : null}
        {!hasReflectionError ? (
          <MomentsCard title="Session Reflections">
            {renderReflectionBody(isLoading, rows)}
            {shouldShowReflectionFooter ? (
              <div className="border-0 border-t border-solid border-cement-200 px-8 py-5">
                <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
                  <Typography className="text-cement-400" variant="caption">
                    {sessionsShownLabel}
                  </Typography>
                  <div className="flex w-full flex-wrap justify-end gap-3 md:w-auto">
                    <RouterLink
                      className={footerLinkClass}
                      to={allHighlightsHref}
                      onClick={() => {
                        momentsAnalytics.track(
                          MEMBER_MOMENTS_EVENTS.viewedHighlights,
                          buildMemberMomentsAnalyticsProperties({
                            destination: allHighlightsHref,
                            entrypoint: 'home_footer',
                            highlightCount: highlightsTotalCount,
                            route: window.location.pathname,
                            surface: 'home',
                          }),
                        );
                      }}
                    >
                      {allHighlightsLabel(highlightsTotalCount)}
                    </RouterLink>
                    <RouterLink
                      className={footerLinkClass}
                      to={showAllHref}
                      onClick={() => {
                        momentsAnalytics.track(
                          MEMBER_MOMENTS_EVENTS.clickedHomeReflection,
                          buildMemberMomentsAnalyticsProperties({
                            destination: showAllHref,
                            route: window.location.pathname,
                            surface: 'home',
                          }),
                        );
                      }}
                    >
                      All Reflections
                    </RouterLink>
                  </div>
                </div>
              </div>
            ) : null}
          </MomentsCard>
        ) : null}
      </div>
    </section>
  );
};

export default SessionReflectionsModule;
